import { DateTime } from 'luxon';
import { Frequency, RRule } from 'rrule';
import ENGLISH from 'rrule/dist/esm/nlp/i18n';
import { TIME_ZONE_UTC } from '@/constants';
import type { ExperienceTimeFormat } from '@/types/gql.generated';
import { isMultiDay } from '@/utils/dates';
import {
  formatTime,
  formatTimeRange,
  getTimeFormatToken,
} from '@/utils/formatTime';

export const defaultDateFormat = 'ccc, MMM d';
export const defaultDateFormatWithYear = 'ccc, MMM d, y';

export type Options = {
  dateFormat?: string;
  dateFormatWithYear?: string;
  isUpcoming?: boolean;
  displayTimezone?: string;
  includeAllDay?: boolean;
  includeEndTime?: boolean;
  includeTime?: boolean;
  includeTimezone?: boolean;
  includeYear?: boolean;
  forceSingleDay?: boolean;
  timeFormat?: ExperienceTimeFormat;
};

// --- Helper Functions ---

export const getDayOfWeek = (
  date: DateTime,
  format: 'short' | 'long' = 'short'
): string => date.toFormat(format === 'short' ? 'ccc' : 'cccc'); // 'Mon' or 'Monday'

export const getMonth = (
  date: DateTime,
  format: 'short' | 'long' = 'long'
): string => date.toFormat(format === 'short' ? 'LLL' : 'LLLL'); // 'Jan' or 'January'

export const getYear = (date: DateTime): string => date.toFormat('yyyy');

export const getDayOfMonth = (date: DateTime): string => date.toFormat('d');

export const getTime = (date: DateTime, withMeridiem = true): string =>
  date
    .toFormat(withMeridiem ? 'h:mm a' : 'H:mm')
    .replace(/:00/g, '')
    .toLowerCase();

export const getDateString = (
  date: DateTime,
  {
    includeYear = true,
    format = 'ccc, MMM d',
  }: { includeYear?: boolean; format?: string } = {}
): string => {
  const hasYear = /(^|[^yY])[yY]+/.test(format);
  const fmt = includeYear && !hasYear ? `${format}, yyyy` : format;
  return date.toFormat(fmt);
};

export const getTimezone = (date: DateTime): string => date.offsetNameShort;

// --- Main formatting Functions ---

/**
 * Whether the rule itself set BYMONTHDAY. `options` cannot answer this: RRule
 * normalizes a bare monthly rule by filling the day in from `dtstart`, so it
 * reports one either way. `origOptions` holds only what was written.
 */
const hasExplicitMonthDay = (rrule: RRule): boolean => {
  const { bymonthday } = rrule.origOptions;
  return Array.isArray(bymonthday) ? bymonthday.length > 0 : bymonthday != null;
};

// Handles multi-day events and all-day events
const getMultiDayDateSummary = (
  startDate: DateTime,
  endDate: DateTime,
  isOnDay: boolean,
  timeFormat?: ExperienceTimeFormat
): string => {
  const isSameYear = endDate.hasSame(startDate, 'year');
  const startDateFormat = isSameYear
    ? defaultDateFormat
    : defaultDateFormatWithYear;
  const endDateFormat = defaultDateFormatWithYear;

  if (isOnDay) {
    const start = startDate.toFormat(startDateFormat);
    const end = endDate.minus({ day: 1 }).toFormat(endDateFormat);
    return `${start} - ${end}, all day`;
  }

  const token = getTimeFormatToken(timeFormat);
  const toFormat = (date: DateTime, format: string): string => {
    // Explicit per-view format: render date and time separately so the time
    // mirrors the picker exactly (WYSIWYG). Legacy (no format) keeps the
    // combined, compact styling.
    if (timeFormat !== undefined) {
      return `${date.toFormat(format)} at ${formatTime(date, timeFormat)}`;
    }
    return date
      .toFormat(`${format} 'at' ${token}`)
      .replace(/AM|PM/g, ($1) => $1.toLowerCase())
      .replace(/:00/g, '');
  };

  const start = toFormat(startDate, startDateFormat);
  const end = toFormat(endDate, endDateFormat);

  return `${start} - ${end}`;
};

// date objects in rrule.js are always in UTC so parse them as such,
// then convert back to the zone that the schedule is being displayed in
const fromJSDate = (date: Date, timeZone: string) =>
  DateTime.fromJSDate(date, { zone: 'UTC' }).setZone(timeZone);
const getTimeSummary = (
  startDate: DateTime,
  endDate: DateTime,
  isOnDay: boolean,
  includeAllDay = true,
  includeEndTime = true,
  timeFormat?: ExperienceTimeFormat
) => {
  if (isOnDay) {
    return includeAllDay ? 'all day' : '';
  }

  return formatTimeRange(startDate, endDate, timeFormat, { includeEndTime });
};

/**
 * Time-only range for a single event (e.g. "12 - 4 pm"), honoring the
 * experience's time format and end-time preference. Returns an empty string for
 * all-day events so callers can omit the field. Used by list-row layouts that
 * render the time in its own column, separate from the date.
 */
export const getTimeRange = (
  startDate: DateTime,
  endDate: DateTime,
  isOnDay: boolean,
  options?: Pick<Options, 'includeEndTime' | 'timeFormat'>
): string =>
  getTimeSummary(
    startDate,
    endDate,
    isOnDay,
    false,
    options?.includeEndTime ?? true,
    options?.timeFormat
  );

export const getDateSummary = (
  startDate: DateTime,
  endDate: DateTime,
  isOnDay: boolean,
  rule?: string | null,
  options?: Options
): string => {
  if (isMultiDay(startDate, endDate)) {
    return getMultiDayDateSummary(
      startDate,
      endDate,
      isOnDay,
      options?.timeFormat
    );
  }

  const dateFormat = options?.dateFormat ?? defaultDateFormat;
  const dateFormatWithYear =
    options?.dateFormatWithYear ?? defaultDateFormatWithYear;

  const time = getTimeSummary(
    startDate,
    endDate,
    isOnDay,
    true,
    options?.includeEndTime ?? true,
    options?.timeFormat
  );
  const timeZone = startDate.zoneName;
  let str = '';

  if (rule) {
    const rrule = RRule.fromString(rule);
    const { freq, until, byweekday, bynweekday } = rrule.options;
    const today = DateTime.local({
      zone: options?.displayTimezone ?? timeZone,
    }).startOf('day');
    const isStartDateHidden = Boolean(options?.isUpcoming && startDate < today);

    // optimize for daily recurrence disguised as weekly
    if (
      freq === Frequency.WEEKLY &&
      Array.isArray(byweekday) &&
      byweekday.length === 7
    ) {
      if (!isStartDateHidden) {
        const startStr = startDate.toFormat(dateFormatWithYear);
        str = `every day starting ${startStr}`;
      }

      if (until) {
        const untilStr = fromJSDate(
          until,
          isOnDay ? TIME_ZONE_UTC : timeZone
        ).toFormat(dateFormat);
        str = `${str} until ${untilStr}`;
      }

      return `${str}, ${time}`;
    }

    str = rrule.toText(
      (text) => {
        if (typeof text !== 'string') {
          return String(text);
        }
        // "Every day starting X"
        if (!until && ['day', 'year'].includes(text)) {
          return `${text} starting ${startDate.toFormat(dateFormatWithYear)}`;
        }
        // "Every day from X to Y"
        if (text === 'until') {
          if (!isStartDateHidden) {
            return `from ${startDate.toFormat(dateFormat)} to`;
          } else {
            return 'until';
          }
        }
        return text;
      },
      {
        ...ENGLISH,
        dayNames: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      },
      // rrule calls this only to render UNTIL, and it passes the date it derived
      // from that instant in UTC — by which point the time of day, and so the
      // local date, is gone. An end of Jan 15 in Chicago is stored as Jan 16
      // UTC, and would read back as Jan 16. Formatting the instant itself keeps
      // the date the author picked. The locale follows startDate so the two
      // halves of the sentence do not come back in different languages.
      () =>
        fromJSDate(until!, isOnDay ? TIME_ZONE_UTC : timeZone)
          .setLocale(startDate.locale)
          .toFormat(dateFormatWithYear)
    );

    if (freq === Frequency.WEEKLY && !until && !isStartDateHidden) {
      const start = startDate.toFormat(dateFormatWithYear);
      str = `${str} starting ${start}`;
    }

    // A bare monthly rule leaves the day of the month implied by dtstart, and
    // rrule's toText renders it as just "every month" — so name the day. Any
    // BY* part that selects the day has already been named by toText: a plain
    // BYDAY lands in byweekday ("every month on Mon"), an ordinal one in
    // bynweekday ("on the 1st Tue"), and BYMONTHDAY in the rule's own options.
    if (
      freq === Frequency.MONTHLY &&
      !until &&
      !bynweekday &&
      !byweekday?.length &&
      !hasExplicitMonthDay(rrule)
    ) {
      str = `${str} on day ${startDate.day}`;
    }
  } else {
    str = startDate.toFormat(dateFormatWithYear);
  }

  str = `${str}, ${time}`;

  return str;
};

const getMultiDayDateSummaryWithFormat = (
  startDate: DateTime,
  endDate: DateTime,
  isOnDay: boolean,
  options?: Options
): string => {
  const dateFormatBase = options?.dateFormat ?? defaultDateFormat;
  const dateFormatWithYearBase =
    options?.dateFormatWithYear ?? defaultDateFormatWithYear;
  const includeYear = options?.includeYear ?? true;
  const includeTime = options?.includeTime ?? true;
  const includeTimezone = options?.includeTimezone ?? false;
  const includeAllDay = options?.includeAllDay ?? false;

  const startDateFormat = includeYear ? dateFormatWithYearBase : dateFormatBase;
  const endDateFormat = includeYear ? dateFormatWithYearBase : dateFormatBase;

  if (isOnDay) {
    const start = startDate.toFormat(startDateFormat);
    const end = endDate.minus({ day: 1 }).toFormat(endDateFormat);
    let result = `${start} - ${end}`;
    if (includeAllDay) result += `, all day`;
    if (includeTimezone) result += ` ${startDate.offsetNameShort}`;
    return result;
  }

  const timeToken = getTimeFormatToken(options?.timeFormat);
  const toFormat = (date: DateTime, format: string): string => {
    if (!includeTime) {
      return date.toFormat(format);
    }
    // Explicit per-view format: render date and time separately so the time
    // mirrors the picker exactly (WYSIWYG). Legacy (no format) keeps the
    // combined, compact styling.
    if (options?.timeFormat !== undefined) {
      return `${date.toFormat(format)} at ${formatTime(
        date,
        options.timeFormat
      )}`;
    }
    return date
      .toFormat(`${format} 'at' ${timeToken}`)
      .replace(/AM|PM/g, ($1) => $1.toLowerCase())
      .replace(/:00/g, '');
  };

  const start = toFormat(startDate, startDateFormat);
  const end = toFormat(endDate, endDateFormat);

  let result = `${start} - ${end}`;
  if (includeTimezone) {
    result += ` ${startDate.offsetNameShort}`;
  }
  return result;
};

export const getDateSummaryWithFormat = (
  startDate: DateTime,
  endDate: DateTime,
  isOnDay: boolean,
  rule?: string | null,
  options?: Options
): string => {
  const forceSingleDay = options?.forceSingleDay ?? false;

  if (!forceSingleDay && isMultiDay(startDate, endDate)) {
    return getMultiDayDateSummaryWithFormat(
      startDate,
      endDate,
      isOnDay,
      options
    );
  }

  const dateFormatBase = options?.dateFormat ?? defaultDateFormat;
  const dateFormatWithYearBase =
    options?.dateFormatWithYear ?? defaultDateFormatWithYear;
  const includeYear = options?.includeYear ?? true;
  const includeTimezone = options?.includeTimezone ?? false;
  const includeTime = options?.includeTime ?? true;
  const includeAllDay = options?.includeAllDay ?? false;
  const includeEndTime = options?.includeEndTime ?? true;

  const dateFormat = includeYear ? dateFormatWithYearBase : dateFormatBase;

  const time = includeTime
    ? getTimeSummary(
        startDate,
        endDate,
        isOnDay,
        includeAllDay,
        includeEndTime,
        options?.timeFormat
      )
    : '';
  const timeZone = startDate.zoneName;
  let str = '';

  if (rule) {
    const rrule = RRule.fromString(rule);
    const { freq, until, byweekday, bynweekday } = rrule.options;
    const today = DateTime.local({
      zone: options?.displayTimezone ?? timeZone,
    }).startOf('day');
    const isStartDateHidden = Boolean(options?.isUpcoming && startDate < today);

    // optimize for daily recurrence disguised as weekly
    if (freq === Frequency.WEEKLY && byweekday.length === 7) {
      if (!isStartDateHidden) {
        const startStr = startDate.toFormat(dateFormat);
        str = `every day starting ${startStr}`;
      }

      if (until) {
        const untilStr = fromJSDate(
          until,
          isOnDay ? TIME_ZONE_UTC : timeZone
        ).toFormat(dateFormat);
        str = `${str} until ${untilStr}`;
      }

      str = time ? `${str}, ${time}` : str;
      if (includeTimezone) {
        str = `${str} ${startDate.offsetNameShort}`;
      }
      return str.trim();
    }

    str = rrule.toText(
      (text) => {
        if (typeof text !== 'string') {
          return String(text);
        }
        // "Every day starting X"
        if (!until && ['day', 'year'].includes(text)) {
          return `${text} starting ${startDate.toFormat(dateFormat)}`;
        }
        // "Every day from X to Y"
        if (text === 'until') {
          if (!isStartDateHidden) {
            return `from ${startDate.toFormat(dateFormat)} to`;
          } else {
            return 'until';
          }
        }
        return text;
      },
      {
        ...ENGLISH,
        dayNames: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      },
      // rrule hands this a month name, not a number, so it has to be parsed as
      // one — the same way getDateSummary's formatter above does.
      // Same as above: the instant, not the UTC date rrule derived from it.
      () =>
        fromJSDate(until!, isOnDay ? TIME_ZONE_UTC : timeZone)
          .setLocale(startDate.locale)
          .toFormat(dateFormat)
    );

    if (freq === Frequency.WEEKLY && !until && !isStartDateHidden) {
      const start = startDate.toFormat(dateFormat);
      str = `${str} starting ${start}`;
    }

    // A bare monthly rule leaves the day of the month implied by dtstart, and
    // rrule's toText renders it as just "every month" — so name the day. Any
    // BY* part that selects the day has already been named by toText: a plain
    // BYDAY lands in byweekday ("every month on Mon"), an ordinal one in
    // bynweekday ("on the 1st Tue"), and BYMONTHDAY in the rule's own options.
    if (
      freq === Frequency.MONTHLY &&
      !until &&
      !bynweekday &&
      !byweekday?.length &&
      !hasExplicitMonthDay(rrule)
    ) {
      str = `${str} on day ${startDate.day}`;
    }
  } else {
    str = startDate.toFormat(dateFormat);
  }

  str = time ? `${str}, ${time}` : str;
  if (includeTimezone) {
    str = `${str} ${startDate.offsetNameShort}`;
  }

  return str.trim();
};
